import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  CatalogRepPromotionalAuTerritoriesEnum,
  CatalogRepPromotionalNzTerritoriesEnum
} from '../../../../../../__generated__/globalTypes'

import CheckboxRadio from 'components/CheckboxRadio'

import styles from './PromotionLocationsCheckList.module.css'

type PromotionLocationsCheckListProps = {
  catalogId: string
  auTerritories?: CatalogRepPromotionalAuTerritoriesEnum[] | null
  nzTerritories?: CatalogRepPromotionalNzTerritoriesEnum[] | null
  toggleTerritory: (
    id: string,
    country: 'AU' | 'NZ',
    territory: CatalogRepPromotionalAuTerritoriesEnum | CatalogRepPromotionalNzTerritoriesEnum
  ) => void
  setAUTerritories: (id: string) => void
  setNZTerritories: (id: string) => void
  unsetAUTerritories: (id: string) => void
  unsetNZTerritories: (id: string) => void
  setNoNeedSalesReps?: () => void
  unsetNoNeedSalesReps?: () => void
}

const PromotionLocationsCheckList = ({
  catalogId,
  auTerritories,
  nzTerritories,
  toggleTerritory,
  setAUTerritories,
  setNZTerritories,
  unsetAUTerritories,
  unsetNZTerritories,
  setNoNeedSalesReps,
  unsetNoNeedSalesReps
}: PromotionLocationsCheckListProps) => {
  const [noRepsNeededChecked, setNoRepsNeededChecked] = useState<boolean>(false)

  const allAUChecked = useMemo(() => {
    return (
      Array.isArray(auTerritories) &&
      [...auTerritories].sort().toString() === Object.values(CatalogRepPromotionalAuTerritoriesEnum).toString()
    )
  }, [auTerritories])

  const allNZChecked = useMemo(() => {
    return (
      Array.isArray(nzTerritories) &&
      [...nzTerritories].sort().toString() === Object.values(CatalogRepPromotionalNzTerritoriesEnum).toString()
    )
  }, [nzTerritories])

  const toggleAUTerritories = useCallback(() => {
    if (allAUChecked) {
      unsetAUTerritories(catalogId)
    } else {
      setAUTerritories(catalogId)
    }
  }, [allAUChecked, catalogId, setAUTerritories, unsetAUTerritories])

  const toggleNZTerritories = useCallback(() => {
    if (allNZChecked) {
      unsetNZTerritories(catalogId)
    } else {
      setNZTerritories(catalogId)
    }
  }, [allNZChecked, catalogId, setNZTerritories, unsetNZTerritories])

  const toggleNoRepsOption = useCallback(() => {
    if (noRepsNeededChecked) {
      unsetNoNeedSalesReps && unsetNoNeedSalesReps()
    } else {
      setNoNeedSalesReps && setNoNeedSalesReps()
    }

    setNoRepsNeededChecked(prevState => !prevState)
  }, [noRepsNeededChecked, setNoNeedSalesReps, unsetNoNeedSalesReps])

  const isAUTerritoryChecked = useMemo(() => {
    return (territory: CatalogRepPromotionalAuTerritoriesEnum) =>
      Array.isArray(auTerritories) && auTerritories.includes(territory)
  }, [auTerritories])

  const isNZTerritoryChecked = useMemo(() => {
    return (territory: CatalogRepPromotionalNzTerritoriesEnum) =>
      Array.isArray(nzTerritories) && nzTerritories.includes(territory)
  }, [nzTerritories])

  useEffect(() => {
    if ((auTerritories?.length ?? [].length) > 0 || (nzTerritories?.length ?? [].length) > 0) {
      setNoRepsNeededChecked(false)
    }
  }, [auTerritories?.length, nzTerritories?.length])

  return (
    <div className={styles.container}>
      <p className={styles.title}>Choose where you need Sales Reps</p>

      <div className={styles.checkboxTerritorySection}>
        <CheckboxRadio
          className={styles.country}
          type="checkbox"
          onChange={toggleAUTerritories}
          checked={allAUChecked}
          name="australia"
          label="Australia"
        />
        <div className={styles.checkboxListContainer}>
          <div className={styles.checkboxListColumn}>
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.ACT)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.ACT)}
              name="act"
              label="Australian Capital Territory"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.NSW)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.NSW)}
              name="nsw"
              label="New South Wales"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.NT)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.NT)}
              name="nt"
              label="Northern Territory"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.QLD)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.QLD)}
              name="qld"
              label="Queensland"
            />
          </div>
          <div className={styles.checkboxListColumn}>
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.SA)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.SA)}
              name="sa"
              label="South Australia"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.TAS)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.TAS)}
              name="tas"
              label="Tasmania"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.VIC)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.VIC)}
              name="vic"
              label="Victoria"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'AU', CatalogRepPromotionalAuTerritoriesEnum.WA)}
              checked={isAUTerritoryChecked(CatalogRepPromotionalAuTerritoriesEnum.WA)}
              name="wa"
              label="Western Australia"
            />
          </div>
        </div>
      </div>

      <div className={styles.checkboxTerritorySection}>
        <CheckboxRadio
          className={styles.country}
          type="checkbox"
          onChange={toggleNZTerritories}
          checked={allNZChecked}
          name="new-zealand"
          label="New Zealand"
        />
        <div className={styles.checkboxListContainer}>
          <div className={styles.checkboxListColumn}>
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'NZ', CatalogRepPromotionalNzTerritoriesEnum.NI)}
              checked={isNZTerritoryChecked(CatalogRepPromotionalNzTerritoriesEnum.NI)}
              name="ni"
              label="North Island"
            />
            <CheckboxRadio
              type="checkbox"
              onChange={() => toggleTerritory(catalogId, 'NZ', CatalogRepPromotionalNzTerritoriesEnum.SI)}
              checked={isNZTerritoryChecked(CatalogRepPromotionalNzTerritoriesEnum.SI)}
              name="si"
              label="South Island"
            />
          </div>
        </div>
      </div>

      {setNoNeedSalesReps && unsetNoNeedSalesReps && (
        <div className={styles.checkboxTerritorySection}>
          <CheckboxRadio
            className={styles.country}
            type="checkbox"
            onChange={toggleNoRepsOption}
            checked={noRepsNeededChecked}
            name="i-dont-need-sales-reps"
            label="I don't need Sales Reps"
          />
        </div>
      )}
    </div>
  )
}

export default PromotionLocationsCheckList
